import React, { useState } from "react";
import './apiDebug.css';

// Componente para renderizar JSON interactivo con colores y expandir/colapsar
const JsonViewer = ({ data, title, defaultExpanded = false }) => {
  const [expandedPaths, setExpandedPaths] = useState(new Set(defaultExpanded ? ['root'] : []));
  const [copiedPath, setCopiedPath] = useState(null);

  const togglePath = (path) => {
    setExpandedPaths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  const isExpanded = (path) => expandedPaths.has(path);

  const copyToClipboard = (text, path) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedPath(path);
      setTimeout(() => setCopiedPath(null), 2000);
    });
  };

  const renderValue = (value, path, key = null, level = 0) => {
    const currentPath = key ? `${path}.${key}` : path;

    if (value === null) {
      return <span className="json-null">null</span>;
    }

    if (value === undefined) {
      return <span className="json-undefined">undefined</span>;
    }

    if (typeof value === 'boolean') {
      return <span className="json-boolean">{String(value)}</span>;
    }

    if (typeof value === 'number') {
      return <span className="json-number">{value}</span>;
    }

    if (typeof value === 'string') {
      const isLongString = value.length > 100;
      const displayValue = isLongString ? value.substring(0, 100) + '...' : value;
      return (
        <span>
          <span className="json-string">"{displayValue}"</span>
          {isLongString && (
            <span className="json-string-info" title={value}>
              (truncado, {value.length} caracteres)
            </span>
          )}
        </span>
      );
    }

    if (Array.isArray(value)) {
      const isExp = isExpanded(currentPath);
      const isEmpty = value.length === 0;

      return (
        <div className="json-array">
          <span 
            className="json-toggle" 
            onClick={() => !isEmpty && togglePath(currentPath)}
            style={{ cursor: isEmpty ? 'default' : 'pointer' }}
          >
            {!isEmpty && (isExp ? '▼' : '▶')}
            <span className="json-bracket">[</span>
          </span>
          {isEmpty ? (
            <span className="json-empty">vacío</span>
          ) : (
            <>
              {!isExp && <span className="json-count"> {value.length} elemento{value.length !== 1 ? 's' : ''}</span>}
              {isExp && (
                <div className="json-children">
                  {value.map((item, index) => (
                    <div key={`${currentPath}-${index}`} className="json-item">
                      <span className="json-key">{index}:</span>
                      {renderValue(item, currentPath, index, level + 1)}
                      {index < value.length - 1 && <span className="json-comma">,</span>}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          <span className="json-bracket">]</span>
        </div>
      );
    }

    if (typeof value === 'object') {
      const keys = Object.keys(value);
      const isExp = isExpanded(currentPath);
      const isEmpty = keys.length === 0;

      return (
        <div className="json-object">
          <span 
            className="json-toggle" 
            onClick={() => !isEmpty && togglePath(currentPath)}
            style={{ cursor: isEmpty ? 'default' : 'pointer' }}
          >
            {!isEmpty && (isExp ? '▼' : '▶')}
            <span className="json-bracket">{'{'}</span>
          </span>
          {isEmpty ? (
            <span className="json-empty">vacío</span>
          ) : (
            <>
              {!isExp && <span className="json-count"> {keys.length} propiedad{keys.length !== 1 ? 'es' : ''}</span>}
              {isExp && (
                <div className="json-children">
                  {keys.map((k, index) => (
                    <div key={`${currentPath}-${k}`} className="json-item">
                      <span className="json-key">"{k}"</span>
                      <span className="json-colon">:</span>
                      {renderValue(value[k], currentPath, k, level + 1)}
                      {index < keys.length - 1 && <span className="json-comma">,</span>}
                      <button
                        className="json-copy-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(JSON.stringify(value[k], null, 2), `${currentPath}.${k}`);
                        }}
                        title="Copiar valor"
                      >
                        {copiedPath === `${currentPath}.${k}` ? '✓' : '📋'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          <span className="json-bracket">{'}'}</span>
        </div>
      );
    }

    return <span className="json-unknown">{String(value)}</span>;
  };

  const renderJson = () => {
    try {
      if (data === null || data === undefined) {
        return <span className="json-null">null</span>;
      }
      return renderValue(data, 'root', null, 0);
    } catch (e) {
      return <span className="json-error">Error al renderizar: {e.message}</span>;
    }
  };

  const expandAll = () => {
    const allPaths = new Set();
    const collectPaths = (obj, path = 'root') => {
      allPaths.add(path);
      if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
          if (typeof item === 'object' && item !== null) {
            collectPaths(item, `${path}.${index}`);
          }
        });
      } else if (typeof obj === 'object' && obj !== null) {
        Object.keys(obj).forEach(key => {
          const value = obj[key];
          if (typeof value === 'object' && value !== null) {
            collectPaths(value, `${path}.${key}`);
          }
        });
      }
    };
    collectPaths(data);
    setExpandedPaths(allPaths);
  };

  const collapseAll = () => {
    setExpandedPaths(new Set());
  };

  return (
    <div className="json-viewer-container">
      {title && <div className="json-viewer-title">{title}</div>}
      <div className="json-viewer-controls">
        <button className="json-control-btn" onClick={expandAll} title="Expandir todo">
          ▼ Expandir todo
        </button>
        <button className="json-control-btn" onClick={collapseAll} title="Colapsar todo">
          ▶ Colapsar todo
        </button>
        <button 
          className="json-control-btn" 
          onClick={() => copyToClipboard(JSON.stringify(data, null, 2), 'all')}
          title="Copiar JSON completo"
        >
          {copiedPath === 'all' ? '✓ Copiado' : '📋 Copiar JSON'}
        </button>
      </div>
      <div className="json-viewer-content-interactive">
        {renderJson()}
      </div>
    </div>
  );
};

// Componente para secciones colapsables mejoradas
const CollapsibleSection = ({ 
  title, 
  isExpanded, 
  onToggle, 
  children, 
  icon = null,
  badge = null,
  headerColor = '#e2e2e2cc'
}) => {
  return (
    <div className="collapsible-section">
      <div 
        className="collapsible-header" 
        onClick={onToggle}
        style={{ backgroundColor: headerColor }}
      >
        <div className="collapsible-header-content">
          {icon && <span className="section-icon">{icon}</span>}
          <h3 className="collapsible-title">{title}</h3>
          {badge && <span className="section-badge">{badge}</span>}
        </div>
        <span className="collapse-icon">
          {isExpanded ? '▼' : '▶'}
        </span>
      </div>
      {isExpanded && (
        <div className="collapsible-content">
          {children}
        </div>
      )}
    </div>
  );
};

export default function DataNotificationProcess({ data, index }) {
  const [expandedSections, setExpandedSections] = useState({
    basicInfo: false,
    notificationBody: false,
    resourceData: false,
    billingInfo: false,
    shippingData: false,
    product: false,
    merchant: false,
    trace: false,
    times: false
  });

  const [isAllExpanded, setIsAllExpanded] = useState(false);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleAllSections = () => {
    const newState = !isAllExpanded;
    setIsAllExpanded(newState);
    setExpandedSections({
      basicInfo: newState,
      notificationBody: newState,
      resourceData: newState,
      billingInfo: newState,
      shippingData: newState,
      product: newState,
      merchant: newState,
      trace: newState,
      times: newState
    });
  };

  if (!data) {
    return (
      <div className="no-data-message">
        <p>No hay datos disponibles</p>
      </div>
    );
  }

  // Obtener información del order si existe
  const orderId = data.data?.resourceData?.identifierNumber || 
                  data.data?.resourceData?.orderId || 
                  data.data?.resourceData?.cashea?.orderId ||
                  data.data?.resourceData?.id || 
                  data.data?.notificationBody?.orderId ||
                  data.data?.notificationBody?.resource?.split('/').pop() || 
                  'N/A';
  // Manejar status que puede ser un objeto {name: "IN_PROGRESS"} o un string
  const statusRaw = data.data?.resourceData?.status || data.data?.resourceData?.cashea?.status || 'N/A';
  const orderStatus = typeof statusRaw === 'object' && statusRaw !== null ? statusRaw.name : statusRaw;
  const topic = data.data?.notificationBody?.topic || 'N/A';

  return (
    <div className="api-data-viewer w-100">
      {/* Header del registro */}
      <div className="record-header">
        <div className="record-header-content">
          <h2 className="record-title">
            <span className="record-index">#{index + 1}</span>
            Detalle de Notification Process
          </h2>
          <div className="record-badges">
            <span className="status-badge status-active">
              {data.plugin?.toUpperCase() || 'N/A'}
            </span>
            {data.route && (
              <span className="status-badge status-active">
                Route: {data.route}
              </span>
            )}
            {orderStatus !== 'N/A' && (
              <span className={`status-badge ${orderStatus === 'paid' || orderStatus === 'confirmed' ? 'status-active' : 'status-inactive'}`}>
                {orderStatus}
              </span>
            )}
          </div>
        </div>
        <button 
          className="expand-all-btn"
          onClick={toggleAllSections}
          title={isAllExpanded ? 'Colapsar todo' : 'Expandir todo'}
        >
          {isAllExpanded ? '▼ Colapsar todo' : '▶ Expandir todo'}
        </button>
      </div>
      
      {/* Sección de Información Básica */}
      <CollapsibleSection
        title="Información Básica"
        isExpanded={expandedSections.basicInfo}
        onToggle={() => toggleSection('basicInfo')}
        icon="📋"
        headerColor="#e2e2e2cc"
      >
        <div className="section-content">
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">ID:</span>
              <span className="detail-value detail-id">{data._id}</span>
            </div>
            {data.requestId && (
              <div className="detail-item">
                <span className="detail-label">Request ID:</span>
                <span className="detail-value detail-code">{data.requestId}</span>
              </div>
            )}
            <div className="detail-item">
              <span className="detail-label">Plugin:</span>
              <span className="detail-value detail-code">{data.plugin || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Route:</span>
              <span className="detail-value">{data.route || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Order ID:</span>
              <span className="detail-value">{orderId}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Topic:</span>
              <span className="detail-value">{topic}</span>
            </div>
            {orderStatus !== 'N/A' && (
              <div className="detail-item">
                <span className="detail-label">Estado del Order:</span>
                <span className={`status-value ${orderStatus === 'paid' || orderStatus === 'confirmed' ? 'active' : 'inactive'}`}>
                  {orderStatus}
                </span>
              </div>
            )}
          </div>
        </div>
      </CollapsibleSection>

      {/* Sección de Notification Body */}
      {data.data?.notificationBody && (
        <CollapsibleSection
          title="Notification Body"
          isExpanded={expandedSections.notificationBody}
          onToggle={() => toggleSection('notificationBody')}
          icon="📨"
          headerColor="#e2e2e242"
        >
          <div className="section-content">
            <JsonViewer data={data.data.notificationBody} />
          </div>
        </CollapsibleSection>
      )}

      {/* Sección de Resource Data */}
      {data.data?.resourceData && (
        <CollapsibleSection
          title="Resource Data (Order)"
          isExpanded={expandedSections.resourceData}
          onToggle={() => toggleSection('resourceData')}
          icon="📦"
          headerColor="#e2e2e2cc"
        >
          <div className="section-content">
            <JsonViewer data={data.data.resourceData} />
          </div>
        </CollapsibleSection>
      )}

      {/* Sección de Billing Info */}
      {data.data?.billingInfo && data.data.billingInfo !== '' && (
        <CollapsibleSection
          title="Billing Info"
          isExpanded={expandedSections.billingInfo}
          onToggle={() => toggleSection('billingInfo')}
          icon="💳"
          headerColor="#e2e2e2cc"
        >
          <div className="section-content">
            <JsonViewer data={data.data.billingInfo} />
          </div>
        </CollapsibleSection>
      )}

      {/* Sección de Shipping Data */}
      {data.data?.shippingData && (
        <CollapsibleSection
          title="Shipping Data"
          isExpanded={expandedSections.shippingData}
          onToggle={() => toggleSection('shippingData')}
          icon="🚚"
          headerColor="#e2e2e2cc"
        >
          <div className="section-content">
            <JsonViewer data={data.data.shippingData} />
          </div>
        </CollapsibleSection>
      )}

      {/* Sección de Product */}
      {data.data?.product && (
        <CollapsibleSection
          title="Product"
          isExpanded={expandedSections.product}
          onToggle={() => toggleSection('product')}
          icon="📦"
          headerColor="#e2e2e2cc"
        >
          <div className="section-content">
            <JsonViewer data={data.data.product} />
          </div>
        </CollapsibleSection>
      )}

      {/* Sección de Merchant */}
      {data.data?.merchant && Array.isArray(data.data.merchant) && data.data.merchant.length > 0 && (
        <CollapsibleSection
          title="Merchant"
          isExpanded={expandedSections.merchant}
          onToggle={() => toggleSection('merchant')}
          icon="🏪"
          badge={`${data.data.merchant.length} merchant${data.data.merchant.length !== 1 ? 's' : ''}`}
          headerColor="#e2e2e2cc"
        >
          <div className="section-content">
            <JsonViewer data={data.data.merchant} />
          </div>
        </CollapsibleSection>
      )}

      {/* Sección de Trace */}
      {data.trace && Array.isArray(data.trace) && data.trace.length > 0 && (
        <CollapsibleSection
          title="Trace"
          isExpanded={expandedSections.trace}
          onToggle={() => toggleSection('trace')}
          icon="🔍"
          badge={`${data.trace.length} paso${data.trace.length !== 1 ? 's' : ''}`}
          headerColor="#e2e2e242"
        >
          <div className="section-content">
            <div className="trace-list">
              {data.trace.map((traceItem, idx) => {
                if (typeof traceItem === 'string') {
                  return (
                    <div key={idx} className="trace-item">
                      <span className="trace-index">{idx + 1}.</span>
                      <span className="trace-text">{traceItem}</span>
                    </div>
                  );
                } else if (typeof traceItem === 'object') {
                  return (
                    <div key={idx} className="trace-item trace-error">
                      <span className="trace-index">{idx + 1}.</span>
                      <JsonViewer data={traceItem} title={`Error en paso ${idx + 1}`} />
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </div>
        </CollapsibleSection>
      )}

      {/* Sección de Tiempos */}
      <CollapsibleSection
        title="Tiempos"
        isExpanded={expandedSections.times}
        onToggle={() => toggleSection('times')}
        icon="⏰"
        headerColor="#e2e2e2cc"
      >
        <div className="section-content">
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">Creado:</span>
              <span className="detail-value detail-time">
                {data.createdAt ? new Date(data.createdAt).toLocaleString('es-ES', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                }) : 'N/A'}
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Actualizado:</span>
              <span className="detail-value detail-time">
                {data.updatedAt ? new Date(data.updatedAt).toLocaleString('es-ES', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                }) : 'N/A'}
              </span>
            </div>
            {data.createdAt && data.updatedAt && (
              <div className="detail-item">
                <span className="detail-label">Duración:</span>
                <span className="detail-value">
                  {Math.round((new Date(data.updatedAt) - new Date(data.createdAt)) / 1000)} segundos
                </span>
              </div>
            )}
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
}

